// src/pages/Room.js
import React, { useState, useEffect } from 'react';
import {
  useParams,
  useNavigate,
  useSearchParams
} from 'react-router-dom';
import {
  doc,
  getDoc,
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import '../App.css';

export default function Room({ user, userData }) {
  const { roomId }       = useParams();
  const navigate         = useNavigate();
  const [searchParams]   = useSearchParams();
  const pwFromQr         = searchParams.get('pw') || '';

  const [room, setRoom]           = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [inputPw, setInputPw]     = useState('');
  const [questions, setQuestions] = useState([]);
  const [questionText, setQuestionText] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [sortBy, setSortBy] = useState('oldest'); // 정렬 기준 기본값을 'oldest'로 변경
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showSortMenu, setShowSortMenu] = useState(false);

  // 이모지 아바타 목록
  const emojiList = ['😀', '🙂', '😊', '😎', '🤔', '😶', '🤐', '😴'];
  const anonymousEmoji = '👤'; // 익명 사용자를 위한 아이콘

  // 화면 크기 변경 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 화면 클릭 시 드롭다운 메뉴 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSortMenu && !event.target.closest('.sort-dropdown')) {
        setShowSortMenu(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSortMenu]);

  // 1) 방 정보 로드
  useEffect(() => {
    if (!roomId) return;
    (async () => {
      const ref = doc(db, 'rooms', roomId);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        alert('존재하지 않는 방입니다.');
        navigate('/search', { replace: true });
        return;
      }
      setRoom({ id: snap.id, ...snap.data() });
    })();
  }, [roomId, navigate]);

  // 2) QR or owner 자동 입장
  useEffect(() => {
    if (!room) return;
    
    // 방장이면 자동 입장
    if (room.ownerId === user.id) {
      setHasAccess(true);
      return;
    }
    
    // 비밀번호가 없는 방이면 자동 입장
    if (!room.password || room.password === '') {
      setHasAccess(true);
      return;
    }
    
    // QR 코드로 비밀번호 제공된 경우 자동 입장
    if (pwFromQr && pwFromQr === room.password) {
      setHasAccess(true);
    }
  }, [room, pwFromQr, user.id]);

  // 3) 질문 실시간 구독
  useEffect(() => {
    if (!hasAccess) return;
    const col = collection(db, 'rooms', roomId, 'questions');
    const unsub = onSnapshot(col, snap => {
      setQuestions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [hasAccess, roomId]);

  // 4) 비밀번호 수동 입력 입장
  const handleEnterWithPw = () => {
    // 비밀번호가 없는 방은 바로 입장
    if (!room.password || room.password === '') {
      setHasAccess(true);
      return;
    }
    
    // 비밀번호가 있는 방은 비밀번호 확인
    if (inputPw === room.password) {
      setHasAccess(true);
    } else {
      alert('비밀번호가 틀렸습니다.');
    }
  };

  // 5) 질문 제출
  const handleSubmitQuestion = async () => {
    if (!questionText.trim()) return;
    await addDoc(collection(db, 'rooms', roomId, 'questions'), {
      text:       questionText.trim(),
      authorId:   user.id,
      authorName: isAnonymous ? '익명' : user.nickname,
      authorIconIdx: isAnonymous ? -1 : (user.iconIdx ?? 0), // 익명일 경우 특별한 인덱스(-1) 사용
      isAnonymous: isAnonymous, // 익명 여부를 명시적으로 저장
      isCompleted: false, // 답변 완료 여부
      createdAt:  serverTimestamp()
    });
    setQuestionText('');
    setIsAnonymous(false);
  };

  // 6) 질문 정렬 함수
  const sortQuestions = (questions) => {
    if (sortBy === 'latest') {
      // 최신순 정렬
      return [...questions].sort((a, b) => {
        if (!a.createdAt || !b.createdAt) return 0;
        return b.createdAt.toDate() - a.createdAt.toDate();
      });
    } else if (sortBy === 'oldest') {
      // 오래된순 정렬
      return [...questions].sort((a, b) => {
        if (!a.createdAt || !b.createdAt) return 0;
        return a.createdAt.toDate() - b.createdAt.toDate();
      });
    } else if (sortBy === 'name') {
      // 이름순 정렬
      return [...questions].sort((a, b) => {
        const nameA = a.authorName || '익명';
        const nameB = b.authorName || '익명';
        return nameA.localeCompare(nameB, 'ko');
      });
    }
    return questions;
  };

  // 7) 사용자 아바타 이모지 가져오기
  const getAvatarEmoji = (question) => {
    // 익명 질문인 경우 항상 익명 아이콘 반환
    if (question.isAnonymous === true) {
      return anonymousEmoji;
    }
    
    // authorIconIdx가 -1이면 익명으로 처리 (기존 데이터 호환성)
    if (question.authorIconIdx === -1) {
      return anonymousEmoji;
    }
    
    // 일반 사용자는 아이콘 인덱스에 따른 이모지 반환
    const iconIdx = question.authorIconIdx ?? 0;
    return emojiList[iconIdx % emojiList.length];
  };

  // 8) 질문 답변 완료 체크 토글
  const toggleQuestionComplete = async (questionId, currentState) => {
    if (room.ownerId !== user.id) return; // 방장만 사용 가능
    
    try {
      const questionRef = doc(db, 'rooms', roomId, 'questions', questionId);
      await updateDoc(questionRef, {
        isCompleted: !currentState
      });
    } catch (error) {
      console.error('답변 상태 업데이트 실패:', error);
      alert('답변 상태 업데이트에 실패했습니다.');
    }
  };

  // 9) 방장 여부 확인
  const isRoomOwner = room && user && room.ownerId === user.id;

  // 10) 질문 삭제 함수 추가
  const handleDeleteQuestion = async (questionId) => {
    if (!confirm('정말로 이 질문을 삭제하시겠습니까?')) return;
    
    try {
      const questionRef = doc(db, 'rooms', roomId, 'questions', questionId);
      await deleteDoc(questionRef);
      console.log('질문 삭제 완료:', questionId);
    } catch (error) {
      console.error('질문 삭제 실패:', error);
      alert('질문 삭제에 실패했습니다.');
    }
  };

  // 정렬 옵션에 따른 표시 텍스트 반환
  const getSortLabel = () => {
    switch(sortBy) {
      case 'oldest': return '오래된순';
      case 'latest': return '최신순';
      case 'name': return '이름순';
      default: return '정렬';
    }
  };

  // 로딩 화면
  if (!room) {
    return <div className="container"><p>로딩 중…</p></div>;
  }

  // 접근 전: 비밀번호 입력폼
  if (!hasAccess) {
    return (
      <div style={{ 
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8fafc',
        padding: '1rem'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '1rem',
          padding: '2rem',
          width: '100%',
          maxWidth: '400px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          textAlign: 'center',
          border: '1px solid #f1f5f9'
        }}>
          <h2 style={{
            color: '#1e293b',
            marginTop: 0,
            marginBottom: '1.5rem',
            fontSize: '1.5rem',
            fontWeight: 700
          }}>{room.name}</h2>
          
          {/* 비밀번호가 있는 방에만 비밀번호 입력창 표시 */}
          {room.password && room.password !== '' ? (
            <>
              <div style={{marginBottom: '1.5rem'}}>
                <input
                  type="password"
                  placeholder="비밀번호 입력"
                  value={inputPw}
                  onChange={e => setInputPw(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    borderRadius: '0.5rem',
                    border: '1px solid #e2e8f0',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                  }}
                />
              </div>
              <button 
                onClick={handleEnterWithPw}
                style={{
                  background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 2rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  width: '100%',
                  boxShadow: '0 2px 4px rgba(99, 102, 241, 0.25)',
                  transition: 'transform 0.1s, box-shadow 0.1s',
                }}
              >
                입장하기
              </button>
            </>
          ) : (
            <button 
              onClick={() => setHasAccess(true)}
              style={{
                background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                color: 'white',
                border: 'none',
                padding: '0.75rem 2rem',
                borderRadius: '0.5rem',
                fontWeight: '600',
                fontSize: '1rem',
                cursor: 'pointer',
                width: '100%',
                boxShadow: '0 2px 4px rgba(99, 102, 241, 0.25)',
                transition: 'transform 0.1s, box-shadow 0.1s',
              }}
            >
              입장하기
            </button>
          )}
          
          <button 
            onClick={() => navigate('/search')}
            style={{
              marginTop: '1rem',
              background: 'transparent',
              color: '#6b7280',
              border: 'none',
              padding: '0.5rem',
              cursor: 'pointer',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.3rem',
              width: '100%',
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
            홈으로 가기
          </button>
        </div>
      </div>
    );
  }

  // 접근 허용 후: 질문 UI
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      background: '#f8fafc'
    }}>
      {/* 헤더 영역 - 고정 위치 */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'white',
        padding: '0.8rem 1rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        height: '3.6rem',
      }}>
        {/* 홈 버튼 추가 */}
        <button
          onClick={() => navigate('/search')}
          style={{
            background: 'transparent',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0.4rem',
            cursor: 'pointer',
            color: '#4f46e5',
          }}
          title="홈으로 가기"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            <polyline points="9 22 9 12 15 12 15 22"></polyline>
          </svg>
        </button>
        
        <h2 style={{
          fontSize: '1.2rem',
          fontWeight: 600,
          color: '#1e293b',
          margin: 0,
          maxWidth: 'calc(100% - 80px)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          textAlign: 'center',
        }}>
          {room.name}
        </h2>

        {/* 정렬 드롭다운 */}
        <div className="sort-dropdown" style={{position: 'relative'}}>
          <button
            onClick={() => setShowSortMenu(!showSortMenu)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.3rem',
              background: 'transparent',
              border: 'none',
              padding: '0.4rem',
              color: '#4f46e5',
              fontWeight: '500',
              fontSize: '0.9rem',
              cursor: 'pointer',
            }}
          >
            {getSortLabel()}
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
          </button>
          
          {showSortMenu && (
            <div 
              style={{
                position: 'absolute',
                right: 0,
                top: '100%',
                background: 'white',
                borderRadius: '0.5rem',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                padding: '0.5rem',
                zIndex: 200,
                border: '1px solid #e5e7eb',
                minWidth: '120px',
              }}
            >
              <button 
                onClick={() => {
                  setSortBy('oldest');
                  setShowSortMenu(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: sortBy === 'oldest' ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                  color: sortBy === 'oldest' ? '#4f46e5' : '#64748b',
                  border: 'none',
                  padding: '0.6rem 0.8rem',
                  borderRadius: '0.3rem',
                  width: '100%',
                  textAlign: 'left',
                  fontWeight: sortBy === 'oldest' ? '600' : '500',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z"></path><path d="m3 9 2.45-4.9A2 2 0 0 1 7.24 3h9.52a2 2 0 0 1 1.8 1.1L21 9"></path><path d="M12 3v6"></path></svg>
                오래된순
              </button>
              <button 
                onClick={() => {
                  setSortBy('latest');
                  setShowSortMenu(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: sortBy === 'latest' ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                  color: sortBy === 'latest' ? '#4f46e5' : '#64748b',
                  border: 'none',
                  padding: '0.6rem 0.8rem',
                  borderRadius: '0.3rem',
                  width: '100%',
                  textAlign: 'left',
                  fontWeight: sortBy === 'latest' ? '600' : '500',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z"></path><path d="m3 9 2.45-4.9A2 2 0 0 1 7.24 3h9.52a2 2 0 0 1 1.8 1.1L21 9"></path><path d="M12 3v6"></path></svg>
                최신순
              </button>
              <button 
                onClick={() => {
                  setSortBy('name');
                  setShowSortMenu(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: sortBy === 'name' ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                  color: sortBy === 'name' ? '#4f46e5' : '#64748b',
                  border: 'none',
                  padding: '0.6rem 0.8rem',
                  borderRadius: '0.3rem',
                  width: '100%',
                  textAlign: 'left',
                  fontWeight: sortBy === 'name' ? '600' : '500',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                이름순
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 스크롤 가능한 콘텐츠 영역 (네비게이션 바 높이만큼 상단 여백 추가) */}
      <div style={{
        flex: 1,
        width: '100%',
        maxWidth: '100%',
        margin: '0 auto',
        paddingTop: 'calc(3.6rem + 0.5rem)', /* 네비게이션 바 높이 + 패딩 */
        paddingBottom: '5rem',
      }}>
        {/* 질문 폼 카드 */}
        <div style={{
          margin: '0 auto 1.5rem auto',
          padding: '0 1rem',
          maxWidth: '768px',
          width: '100%',
        }}>
          <div style={{
            background: 'white',
            borderRadius: '0.8rem',
            padding: '1.2rem',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            border: '1px solid #f1f5f9',
          }}>
            <h3 style={{
              marginBottom: '1rem',
              fontSize: '1.1rem',
              fontWeight: 600,
              color: '#1e293b',
            }}>질문하기</h3>
            <form className="question-form" onSubmit={e => {e.preventDefault(); handleSubmitQuestion();}}>
              <textarea
                className="input question-input"
                rows={3}
                placeholder="질문을 입력하세요"
                value={questionText}
                onChange={e => setQuestionText(e.target.value)}
                style={{
                  marginBottom: '1rem',
                  width: '100%',
                  padding: '0.8rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #e2e8f0',
                  fontSize: '1rem',
                  resize: 'none',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  ':focus': {
                    borderColor: '#6366f1',
                  },
                }}
              />
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '1rem',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.95rem',
                  color: '#64748b',
                }}>
                  <input 
                    type="checkbox" 
                    checked={isAnonymous} 
                    onChange={e=>setIsAnonymous(e.target.checked)} 
                    style={{
                      width: '16px',
                      height: '16px',
                      accentColor: '#6366f1',
                    }}
                  /> 
                  <span style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                    익명으로 질문하기
                    {isAnonymous && (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(71, 85, 105, 0.15)',
                        width: '26px',
                        height: '26px',
                        borderRadius: '50%',
                        fontSize: '1.1rem',
                        border: '1px solid rgba(71, 85, 105, 0.3)'
                      }}>
                        {anonymousEmoji}
                      </span>
                    )}
                  </span>
                </label>
                <button 
                  type="submit"
                  style={{
                    background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                    color: 'white',
                    border: 'none',
                    padding: '0.6rem 1.2rem',
                    borderRadius: '0.5rem',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(99, 102, 241, 0.25)',
                    transition: 'transform 0.1s, box-shadow 0.1s',
                    ':active': {
                      transform: 'translateY(1px)',
                      boxShadow: '0 1px 2px rgba(99, 102, 241, 0.25)',
                    }
                  }}
                >
                  질문 제출
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* 질문 리스트 섹션 */}
        <div style={{
          padding: '0 1rem',
          maxWidth: '768px',
          margin: '0 auto',
          width: '100%',
        }}>
          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
          }}>
            {questions.length ? sortQuestions(questions).map((q, index) => (
              <li key={q.id} style={{
                background: 'white',
                borderRadius: '0.8rem',
                marginBottom: '0.75rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                border: '1px solid #f1f5f9',
                padding: '1.2rem 1.5rem',
                position: 'relative',
                borderLeft: `4px solid ${q.isCompleted ? '#10b981' : '#6366f1'}`,
                paddingLeft: '2.5rem', // 번호를 위한 왼쪽 패딩 추가
                backgroundColor: q.isCompleted ? 'rgba(243, 244, 246, 0.7)' : 'white', // 답변 완료된 질문 배경색 변경
              }}>
                {/* 질문 번호 */}
                <div style={{
                  position: 'absolute',
                  left: '8px', // 왼쪽 위치 조정
                  top: '1.2rem', // 상단에 고정
                  background: q.isCompleted 
                    ? 'linear-gradient(135deg, #10b981, #059669)' 
                    : 'linear-gradient(135deg, #6366f1, #4f46e5)',
                  color: 'white',
                  width: '32px', // 크기 증가
                  height: '32px', // 크기 증가
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.95rem', // 폰트 크기 
                  fontWeight: 'bold',
                  boxShadow: '0 3px 6px rgba(99, 102, 241, 0.3)',
                  border: '2px solid white'
                }}>
                  {q.isCompleted ? '✓' : index + 1}
                </div>
                
                <div style={{
                  marginBottom: '0.7rem', 
                  display: 'flex', 
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    background: 'rgba(241, 245, 249, 0.5)',
                    padding: '0.4rem 0.8rem',
                    borderRadius: '2rem',
                  }}>
                    <span style={{
                      fontSize: '1.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: q.isAnonymous === true 
                        ? 'rgba(71, 85, 105, 0.15)' 
                        : 'rgba(99, 102, 241, 0.1)',
                      width: '38px', 
                      height: '38px',
                      borderRadius: '50%',
                      border: q.isAnonymous === true 
                        ? '1.5px solid rgba(71, 85, 105, 0.3)' 
                        : 'none',
                      boxShadow: q.isAnonymous === true 
                        ? 'none' 
                        : '0 2px 5px rgba(99, 102, 241, 0.1)',
                    }}>
                      {getAvatarEmoji(q)}
                    </span>
                    <span style={{
                      fontWeight: '600', 
                      fontSize: '0.95rem',
                      color: q.isAnonymous === true ? '#64748b' : '#1e293b'
                    }}>
                      {q.authorName}
                    </span>
                  </div>
                  
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.7rem'}}>
                    {/* 방장만 보이는 답변 완료 체크 버튼 */}
                    {isRoomOwner && (
                      <button 
                        onClick={() => toggleQuestionComplete(q.id, q.isCompleted ?? false)}
                        style={{
                          background: q.isCompleted ? 'rgba(16, 185, 129, 0.1)' : 'rgba(209, 213, 219, 0.3)',
                          border: 'none',
                          borderRadius: '50%',
                          width: '30px',
                          height: '30px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                        title={q.isCompleted ? '답변 완료 취소' : '답변 완료 표시'}
                      >
                        {q.isCompleted ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        )}
                      </button>
                    )}
                    
                    {/* 사용자 자신이 작성한 질문에만 삭제 버튼 표시 */}
                    {user.id === q.authorId && (
                      <button 
                        onClick={() => handleDeleteQuestion(q.id)}
                        style={{
                          background: 'rgba(239, 68, 68, 0.1)',
                          border: 'none',
                          borderRadius: '50%',
                          width: '30px',
                          height: '30px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                        title="질문 삭제"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18"></path>
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                        </svg>
                      </button>
                    )}
                    
                    <span style={{
                      color: '#64748b',
                      fontSize: '0.9rem', 
                      padding: '0.25rem 0.6rem',
                      background: 'rgba(241, 245, 249, 0.4)',
                      borderRadius: '1rem',
                    }}>
                      {q.createdAt && q.createdAt.toDate ? new Date(q.createdAt.toDate()).toLocaleString() : ''}
                    </span>
                  </div>
                </div>
                
                {/* 답변 완료 표시 */}
                {q.isCompleted && (
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                    background: 'rgba(16, 185, 129, 0.1)',
                    color: '#059669',
                    padding: '0.3rem 0.7rem',
                    borderRadius: '1rem',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    marginBottom: '0.7rem'
                  }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
                      <path d="m9 12 2 2 4-4"></path>
                    </svg>
                    답변 완료
                  </div>
                )}
                
                <div style={{
                  marginBottom: q.answer ? "0.8rem" : "0", 
                  paddingLeft: '0.25rem',
                  backgroundColor: q.isCompleted ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.7)',
                  padding: '0.8rem 1rem',
                  borderRadius: '0.75rem',
                  border: '1px solid rgba(226, 232, 240, 0.8)'
                }}>
                  <p style={{margin:0, lineHeight: '1.5'}}>{q.text}</p>
                </div>
                {q.answer && (
                  <div style={{
                    background: '#f8fafc',
                    borderRadius: '0.75rem',
                    padding: '0.8rem 1rem',
                    marginTop: '0.8rem',
                    borderLeft: '3px solid #2563eb',
                    marginLeft: '0.5rem',
                    boxShadow: '0 2px 6px rgba(37, 99, 235, 0.08)'
                  }}>
                    <div style={{
                      fontWeight: 500,
                      marginBottom: '0.4rem',
                      color: '#2563eb',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <span style={{
                        background: 'rgba(37, 99, 235, 0.1)',
                        padding: '0.25rem 0.6rem',
                        borderRadius: '1rem',
                        fontSize: '0.8rem',
                        fontWeight: '600'
                      }}>
                        답변
                      </span>
                      <span style={{
                        color: '#64748b',
                        marginLeft: '0.5rem'
                      }}>
                        {q.answer.authorName}
                      </span>
                    </div>
                    <div>
                      <p style={{margin:0, lineHeight: '1.5'}}>{q.answer.content}</p>
                    </div>
                  </div>
                )}
              </li>
            )) : (
              <li style={{
                textAlign: 'center',
                padding: '2rem 1rem',
                background: 'white',
                borderRadius: '0.8rem',
                border: '1px dashed #cbd5e1',
                color: '#64748b',
                fontSize: '1rem',
              }}>아직 질문이 없습니다.</li>
            )}
          </ul>
          
          {/* 홈으로 가기 버튼 추가 */}
          <div style={{
            padding: '1rem',
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '1rem'
          }}>
            <button
              onClick={() => navigate('/search')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '0.5rem',
                padding: '0.6rem 1.2rem',
                color: '#4f46e5',
                fontWeight: '500',
                cursor: 'pointer',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
              홈으로 가기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
